import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { ScheduleSlot, HorizontalSlot } from '../components/ScheduleSlots';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';
import { Calendar } from '../components/ui/calendar';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import { 
  ChevronLeft, 
  ChevronRight, 
  Copy, 
  Trash2,
  AlertCircle,
  FileDown,
  FileSpreadsheet,
  CalendarPlus,
  Star,
  CalendarIcon,
  Filter,
  MessageSquare,
  Coffee,
  Clock,
  LayoutGrid,
  Rows3,
  Columns3
} from 'lucide-react';
import { format, addDays, startOfWeek, addWeeks, subWeeks, startOfMonth, addMonths, subMonths, getDaysInMonth, getDay, eachDayOfInterval, isSameDay, parseISO } from 'date-fns';
import { fr, de, enUS } from 'date-fns/locale';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EVENT_COLORS = [
  '#6366F1', '#EC4899', '#F59E0B', '#10B981', '#EF4444', '#8B5CF6'
];

const ABSENCE_TYPES = {
  paidLeave: { fr: 'Congé payé', en: 'Paid Leave', de: 'Bezahlter Urlaub' },
  sickLeave: { fr: 'Maladie', en: 'Sick Leave', de: 'Krankheit' },
  unpaidLeave: { fr: 'Sans solde', en: 'Unpaid Leave', de: 'Unbezahlter Urlaub' },
  other: { fr: 'Autre', en: 'Other', de: 'Sonstige' }
};

export default function Schedule() {
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const locale = language === 'de' ? de : language === 'en' ? enUS : fr;
  
  const [viewMode, setViewMode] = useState('week');
  const [layoutMode, setLayoutMode] = useState('vertical'); // 'vertical' (current) or 'horizontal' (new)
  const [currentWeek, setCurrentWeek] = useState(startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [currentMonth, setCurrentMonth] = useState(startOfMonth(new Date()));
  const [schedule, setSchedule] = useState([]);
  const [events, setEvents] = useState([]);
  const [absences, setAbsences] = useState([]);
  const [holidays, setHolidays] = useState({});
  const [team, setTeam] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [totals, setTotals] = useState({ week_hours: 0, month_hours: 0 });
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [copyDialogOpen, setCopyDialogOpen] = useState(false);
  const [correctionDialogOpen, setCorrectionDialogOpen] = useState(false);
  const [eventDialogOpen, setEventDialogOpen] = useState(false);
  const [filterPopoverOpen, setFilterPopoverOpen] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [periodCalendarOpen, setPeriodCalendarOpen] = useState(false);
  const [periodLength, setPeriodLength] = useState(1); // Number of weeks/months
  const [periodUnit, setPeriodUnit] = useState('week'); // 'week' or 'month'
  const [periodStartDate, setPeriodStartDate] = useState(new Date());
  const [editingSlot, setEditingSlot] = useState(null);
  const [editingEvent, setEditingEvent] = useState(null);
  const [draggedSlot, setDraggedSlot] = useState(null);
  const [resizingSlot, setResizingSlot] = useState(null); // For resize functionality
  const [leaveDetails, setLeaveDetails] = useState({}); // Leave details per user
  
  // Copy dialog states
  const [copySourceWeek, setCopySourceWeek] = useState(null);
  const [copyTargetWeeks, setCopyTargetWeeks] = useState([]);
  const [copySourceCalendarOpen, setCopySourceCalendarOpen] = useState(false);
  
  // Work hours configuration
  const [workHours, setWorkHours] = useState({ start: 6, end: 22 });
  
  // Default settings from admin
  const [defaultSettings, setDefaultSettings] = useState({
    default_day_duration: 8.5,
    default_break_duration: 30,
    break_threshold_hours: 6
  });
  
  const [newSlot, setNewSlot] = useState({
    user_id: '',
    date: '',
    start_time: '09:00',
    end_time: '17:00',
    break_minutes: 0,
    comment: ''
  });
  const [correction, setCorrection] = useState({
    actual_start_time: '',
    actual_end_time: '',
    reason: ''
  });
  const [newEvent, setNewEvent] = useState({
    title: '',
    date: '',
    start_time: '09:00',
    end_time: '17:00',
    description: '',
    color: '#6366F1',
    all_day: false
  });

  const isManager = user?.role === 'manager' || user?.role === 'directeur';
  const weekStart = format(currentWeek, 'yyyy-MM-dd');
  
  // Calculate days based on period selector
  const days = useMemo(() => {
    const startDate = startOfWeek(periodStartDate, { weekStartsOn: 1 });
    if (periodUnit === 'week') {
      return Array.from({ length: periodLength * 7 }, (_, i) => addDays(startDate, i));
    } else {
      // Month mode: show full months
      const monthStart = startOfMonth(periodStartDate);
      const totalDays = periodLength * 30; // Approximate
      return Array.from({ length: Math.min(totalDays, 42) }, (_, i) => addDays(monthStart, i));
    }
  }, [periodStartDate, periodLength, periodUnit]);
  
  // Generate time slots
  const generateTimeSlots = useCallback(() => {
    const slots = [];
    for (let h = workHours.start; h < workHours.end; h++) {
      for (let m = 0; m < 60; m += 15) {
        slots.push(`${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`);
      }
    }
    return slots;
  }, [workHours]);
  
  const TIME_SLOTS = generateTimeSlots();
  const HOURS = Array.from({ length: workHours.end - workHours.start }, (_, i) => i + workHours.start);

  // Generate 26 weeks for copy targets
  const generateTargetWeeks = useCallback(() => {
    const weeks = [];
    const startDate = copySourceWeek || currentWeek;
    for (let i = 1; i <= 26; i++) {
      const weekDate = addWeeks(startDate, i);
      weeks.push({
        date: format(weekDate, 'yyyy-MM-dd'),
        label: `${format(weekDate, 'd MMM', { locale })} - ${format(addDays(weekDate, 6), 'd MMM yyyy', { locale })}`
      });
    }
    return weeks;
  }, [copySourceWeek, currentWeek, locale]);

  // Fetch functions
  const fetchWorkHours = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/settings/public`);
      if (res.data.work_start_hour && res.data.work_end_hour) {
        setWorkHours({
          start: parseInt(res.data.work_start_hour) || 6,
          end: parseInt(res.data.work_end_hour) || 22
        });
      }
      // Also fetch default settings
      setDefaultSettings({
        default_day_duration: res.data.default_day_duration || 8.5,
        default_break_duration: res.data.default_break_duration || 30,
        break_threshold_hours: res.data.break_threshold_hours || 6
      });
    } catch (err) {
      // Use default
    }
  }, []);

  const fetchSchedule = useCallback(async () => {
    try {
      // Calculate date range based on period selector
      const startDate = startOfWeek(periodStartDate, { weekStartsOn: 1 });
      let endDate;
      
      if (viewMode === 'week') {
        if (periodUnit === 'week') {
          endDate = addDays(startDate, periodLength * 7);
        } else {
          endDate = addMonths(startDate, periodLength);
        }
      } else {
        endDate = addMonths(currentMonth, 1);
      }
      
      const params = viewMode === 'week' 
        ? { 
            week_start: format(startDate, 'yyyy-MM-dd'),
            week_end: format(endDate, 'yyyy-MM-dd')
          }
        : { month: format(currentMonth, 'yyyy-MM') };
      
      const res = await axios.get(`${API}/schedule`, { params });
      setSchedule(res.data);
    } catch (err) {
      toast.error(t('error'));
    } finally {
      setLoading(false);
    }
  }, [periodStartDate, periodLength, periodUnit, currentMonth, viewMode, t]);

  const fetchEvents = useCallback(async () => {
    try {
      const start_date = viewMode === 'week' 
        ? weekStart 
        : format(currentMonth, 'yyyy-MM-01');
      const end_date = viewMode === 'week'
        ? format(addDays(currentWeek, 7), 'yyyy-MM-dd')
        : format(addMonths(currentMonth, 1), 'yyyy-MM-dd');
      
      const res = await axios.get(`${API}/events`, { params: { start_date, end_date } });
      setEvents(res.data);
    } catch (err) {
      console.error('Error fetching events:', err);
    }
  }, [weekStart, currentWeek, currentMonth, viewMode]);

  const fetchAbsences = useCallback(async () => {
    try {
      const start_date = viewMode === 'week' 
        ? weekStart 
        : format(currentMonth, 'yyyy-MM-01');
      const end_date = viewMode === 'week'
        ? format(addDays(currentWeek, 7), 'yyyy-MM-dd')
        : format(addMonths(currentMonth, 1), 'yyyy-MM-dd');
      
      const res = await axios.get(`${API}/absences/schedule`, { params: { start_date, end_date } });
      setAbsences(res.data);
    } catch (err) {
      console.error('Error fetching absences:', err);
    }
  }, [weekStart, currentWeek, currentMonth, viewMode]);

  const fetchHolidays = useCallback(async () => {
    try {
      const year = viewMode === 'week' ? currentWeek.getFullYear() : currentMonth.getFullYear();
      const res = await axios.get(`${API}/holidays`, { params: { year } });
      setHolidays(res.data);
    } catch (err) {
      console.error('Error fetching holidays:', err);
    }
  }, [currentWeek, currentMonth, viewMode]);

  const fetchTotals = useCallback(async () => {
    try {
      const params = {};
      if (!isManager) {
        params.user_id = user?.id;
      } else if (selectedUsers.length === 1) {
        params.user_id = selectedUsers[0];
      }
      const res = await axios.get(`${API}/stats/totals`, { params });
      setTotals(res.data);
    } catch (err) {
      console.error('Error fetching totals:', err);
    }
  }, [selectedUsers, isManager, user?.id]);

  const fetchLeaveDetails = useCallback(async () => {
    if (!isManager) return;
    try {
      const res = await axios.get(`${API}/stats/leave-balances`);
      const details = {};
      res.data.forEach(item => {
        details[item.user_id] = item;
      });
      setLeaveDetails(details);
    } catch (err) {
      console.error('Error fetching leave details:', err);
    }
  }, [isManager]);

  const fetchTeam = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/team`);
      setTeam(res.data);
      
      // For collaborators, select their own schedule by default
      // For managers/directors, select all users by default
      if (!isManager && user?.id) {
        setSelectedUsers([user.id]);
      } else if (isManager && res.data.length > 0) {
        setSelectedUsers(res.data.map(m => m.id));
      }
    } catch (err) {
      console.error('Error fetching team:', err);
    }
  }, [isManager, user?.id]);

  useEffect(() => {
    fetchWorkHours();
    fetchSchedule();
    fetchEvents();
    fetchAbsences();
    fetchHolidays();
    fetchTotals();
    fetchTeam();
    fetchLeaveDetails();
  }, [fetchWorkHours, fetchSchedule, fetchEvents, fetchAbsences, fetchHolidays, fetchTotals, fetchTeam, fetchLeaveDetails]);

  // Filter schedule
  const filteredSchedule = selectedUsers.length > 0 
    ? schedule.filter(s => selectedUsers.includes(s.user_id))
    : schedule;

  // Filter absences
  const filteredAbsences = selectedUsers.length > 0
    ? absences.filter(a => selectedUsers.includes(a.user_id))
    : absences;

  // Navigation
  const handlePrevWeek = () => setCurrentWeek(subWeeks(currentWeek, 1));
  const handleNextWeek = () => setCurrentWeek(addWeeks(currentWeek, 1));
  const handlePrevMonth = () => setCurrentMonth(subMonths(currentMonth, 1));
  const handleNextMonth = () => setCurrentMonth(addMonths(currentMonth, 1));

  const handleDateSelect = (date) => {
    if (viewMode === 'week') {
      setCurrentWeek(startOfWeek(date, { weekStartsOn: 1 }));
    } else {
      setCurrentMonth(startOfMonth(date));
    }
    setCalendarOpen(false);
  };

  // Filter handlers
  const handleUserFilterChange = (userId, checked) => {
    if (checked) {
      setSelectedUsers([...selectedUsers, userId]);
    } else {
      setSelectedUsers(selectedUsers.filter(id => id !== userId));
    }
  };

  const selectAllUsers = () => setSelectedUsers(team.map(m => m.id));
  const deselectAllUsers = () => {
    if (!isManager && user?.id) {
      setSelectedUsers([user.id]);
    } else {
      setSelectedUsers([]);
    }
  };

  // Cell click
  const handleCellClick = (date, hour) => {
    if (!isManager) return;
    
    // Calculate end time based on default day duration
    const dayDuration = defaultSettings.default_day_duration || 8.5;
    const endHour = Math.min(hour + dayDuration, workHours.end);
    const endHourInt = Math.floor(endHour);
    const endMinutes = Math.round((endHour - endHourInt) * 60);
    
    // Calculate break based on threshold
    const workDuration = endHour - hour;
    const breakMinutes = workDuration >= defaultSettings.break_threshold_hours 
      ? (defaultSettings.default_break_duration || 30) 
      : 0;
    
    setNewSlot({
      user_id: selectedUsers.length === 1 ? selectedUsers[0] : (team[0]?.id || ''),
      date: format(date, 'yyyy-MM-dd'),
      start_time: `${hour.toString().padStart(2, '0')}:00`,
      end_time: `${endHourInt.toString().padStart(2, '0')}:${endMinutes.toString().padStart(2, '0')}`,
      break_minutes: breakMinutes,
      comment: ''
    });
    setEditingSlot(null);
    setDialogOpen(true);
  };

  // Slot click
  const handleSlotClick = (e, slot) => {
    e.stopPropagation();
    
    if (user?.role === 'collaborateur') {
      if (slot.user_id === user.id) {
        setEditingSlot(slot);
        setCorrection({
          actual_start_time: slot.start_time,
          actual_end_time: slot.end_time,
          reason: ''
        });
        setCorrectionDialogOpen(true);
      }
      return;
    }
    
    if (isManager) {
      setEditingSlot(slot);
      setNewSlot({
        user_id: slot.user_id,
        date: slot.date,
        start_time: slot.start_time,
        end_time: slot.end_time,
        break_minutes: slot.break_minutes || 0,
        comment: slot.comment || ''
      });
      setDialogOpen(true);
    }
  };

  // Drag and Drop
  const handleDragStart = (e, slot) => {
    if (!isManager) return;
    setDraggedSlot(slot);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    if (!isManager || !draggedSlot) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, targetDate, targetHour) => {
    e.preventDefault();
    if (!isManager || !draggedSlot) return;

    const newDate = format(targetDate, 'yyyy-MM-dd');
    const duration = (parseInt(draggedSlot.end_time.split(':')[0]) * 60 + parseInt(draggedSlot.end_time.split(':')[1])) -
                    (parseInt(draggedSlot.start_time.split(':')[0]) * 60 + parseInt(draggedSlot.start_time.split(':')[1]));
    
    const newStartTime = `${targetHour.toString().padStart(2, '0')}:00`;
    const endMinutes = targetHour * 60 + duration;
    const newEndTime = `${Math.floor(endMinutes / 60).toString().padStart(2, '0')}:${(endMinutes % 60).toString().padStart(2, '0')}`;

    try {
      await axios.put(`${API}/schedule/${draggedSlot.id}`, {
        date: newDate,
        start_time: newStartTime,
        end_time: newEndTime
      });
      toast.success(t('slotUpdated'));
      fetchSchedule();
      fetchTotals();
    } catch (err) {
      toast.error(t('error'));
    }
    
    setDraggedSlot(null);
  };

  // Save slot
  const handleSaveSlot = async () => {
    try {
      if (editingSlot) {
        await axios.put(`${API}/schedule/${editingSlot.id}`, newSlot);
        toast.success(t('slotUpdated'));
      } else {
        await axios.post(`${API}/schedule`, newSlot);
        toast.success(t('slotAdded'));
      }
      setDialogOpen(false);
      fetchSchedule();
      fetchTotals();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('error'));
    }
  };

  const handleDeleteSlot = async () => {
    if (!editingSlot) return;
    try {
      await axios.delete(`${API}/schedule/${editingSlot.id}`);
      toast.success(t('slotDeleted'));
      setDialogOpen(false);
      fetchSchedule();
      fetchTotals();
    } catch (err) {
      toast.error(t('error'));
    }
  };

  // Correction
  const handleSubmitCorrection = async () => {
    if (!editingSlot) return;
    if (user?.role === 'collaborateur' && editingSlot.user_id !== user.id) {
      toast.error(t('notAuthorized'));
      return;
    }
    try {
      await axios.post(`${API}/corrections`, {
        schedule_id: editingSlot.id,
        actual_start_time: correction.actual_start_time,
        actual_end_time: correction.actual_end_time,
        reason: correction.reason
      });
      toast.success(t('correctionSubmitted'));
      setCorrectionDialogOpen(false);
      fetchSchedule();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('error'));
    }
  };

  // Copy week
  const openCopyDialog = () => {
    setCopySourceWeek(currentWeek);
    setCopyTargetWeeks([]);
    setCopyDialogOpen(true);
  };

  const handleCopyWeek = async () => {
    if (copyTargetWeeks.length === 0) {
      toast.error(t('selectTargetWeeks'));
      return;
    }
    try {
      await axios.post(`${API}/schedule/copy-week`, {
        source_week_start: format(copySourceWeek, 'yyyy-MM-dd'),
        target_week_starts: copyTargetWeeks,
        user_ids: selectedUsers.length > 0 ? selectedUsers : null
      });
      toast.success(t('slotsCopied'));
      setCopyDialogOpen(false);
      fetchSchedule();
    } catch (err) {
      toast.error(t('error'));
    }
  };

  const toggleCopyTargetWeek = (weekDate) => {
    if (copyTargetWeeks.includes(weekDate)) {
      setCopyTargetWeeks(copyTargetWeeks.filter(w => w !== weekDate));
    } else {
      setCopyTargetWeeks([...copyTargetWeeks, weekDate]);
    }
  };

  // Event handlers
  const handleOpenEventDialog = (date = null) => {
    setEditingEvent(null);
    setNewEvent({
      title: '',
      date: date ? format(date, 'yyyy-MM-dd') : format(new Date(), 'yyyy-MM-dd'),
      start_time: '09:00',
      end_time: '17:00',
      description: '',
      color: '#6366F1',
      all_day: false
    });
    setEventDialogOpen(true);
  };

  const handleEventClick = (e, event) => {
    e.stopPropagation();
    if (!isManager) return;
    setEditingEvent(event);
    setNewEvent({
      title: event.title,
      date: event.date,
      start_time: event.start_time || '09:00',
      end_time: event.end_time || '17:00',
      description: event.description || '',
      color: event.color || '#6366F1',
      all_day: event.all_day || false
    });
    setEventDialogOpen(true);
  };

  const handleSaveEvent = async () => {
    try {
      if (editingEvent) {
        await axios.put(`${API}/events/${editingEvent.id}`, newEvent);
        toast.success(t('eventUpdated'));
      } else {
        await axios.post(`${API}/events`, newEvent);
        toast.success(t('eventCreated'));
      }
      setEventDialogOpen(false);
      fetchEvents();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('error'));
    }
  };

  const handleDeleteEvent = async () => {
    if (!editingEvent) return;
    try {
      await axios.delete(`${API}/events/${editingEvent.id}`);
      toast.success(t('eventDeleted'));
      setEventDialogOpen(false);
      fetchEvents();
    } catch (err) {
      toast.error(t('error'));
    }
  };

  // Resize slot functionality
  const handleResizeStart = (e, slot, edge) => {
    if (!isManager) return;
    e.stopPropagation();
    e.preventDefault();
    
    const resizeState = {
      slot,
      edge,
      initialY: e.clientY,
      initialStartTime: slot.start_time,
      initialEndTime: slot.end_time
    };
    
    setResizingSlot(resizeState);
    
    const parseTime = (time) => {
      const [h, m] = time.split(':').map(Number);
      return h * 60 + m;
    };
    
    const formatTime = (minutes) => {
      const h = Math.floor(minutes / 60);
      const m = minutes % 60;
      return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
    };
    
    const handleMove = (moveEvent) => {
      const deltaY = moveEvent.clientY - resizeState.initialY;
      const deltaMinutes = Math.round(deltaY / 48 * 60);
      const roundedMinutes = Math.round(deltaMinutes / 15) * 15;
      
      const initialStart = parseTime(resizeState.initialStartTime);
      const initialEnd = parseTime(resizeState.initialEndTime);
      
      let newStart = initialStart;
      let newEnd = initialEnd;
      
      if (resizeState.edge === 'top') {
        newStart = initialStart + roundedMinutes;
        newStart = Math.min(newStart, initialEnd - 15);
        newStart = Math.max(newStart, workHours.start * 60);
      } else {
        newEnd = initialEnd + roundedMinutes;
        newEnd = Math.max(newEnd, initialStart + 15);
        newEnd = Math.min(newEnd, workHours.end * 60);
      }
      
      setSchedule(prev => prev.map(s => 
        s.id === resizeState.slot.id 
          ? { ...s, start_time: formatTime(newStart), end_time: formatTime(newEnd) }
          : s
      ));
    };
    
    const handleEnd = async () => {
      document.removeEventListener('mousemove', handleMove);
      document.removeEventListener('mouseup', handleEnd);
      
      // Get the current state from DOM
      const currentSchedule = await new Promise(resolve => {
        setSchedule(prev => {
          resolve(prev);
          return prev;
        });
      });
      
      const updatedSlot = currentSchedule.find(s => s.id === resizeState.slot.id);
      
      if (updatedSlot && (updatedSlot.start_time !== resizeState.initialStartTime || updatedSlot.end_time !== resizeState.initialEndTime)) {
        try {
          await axios.put(`${API}/schedule/${updatedSlot.id}`, {
            start_time: updatedSlot.start_time,
            end_time: updatedSlot.end_time
          });
          toast.success(t('slotUpdated'));
          fetchSchedule();
        } catch (err) {
          setSchedule(prev => prev.map(s => 
            s.id === resizeState.slot.id 
              ? { ...s, start_time: resizeState.initialStartTime, end_time: resizeState.initialEndTime }
              : s
          ));
          toast.error(t('error'));
        }
      }
      
      setResizingSlot(null);
    };
    
    document.addEventListener('mousemove', handleMove);
    document.addEventListener('mouseup', handleEnd);
  };

  // Exports - using fetch with auth token to download files
  const handleExportPDF = async () => {
    try {
      let params;
      let filename;
      
      if (viewMode === 'week') {
        // Calculate start and end dates based on period selector
        const startDate = startOfWeek(periodStartDate, { weekStartsOn: 1 });
        let endDate;
        if (periodUnit === 'week') {
          endDate = addDays(startDate, periodLength * 7);
        } else {
          endDate = addMonths(startDate, periodLength);
        }
        const weekStartStr = format(startDate, 'yyyy-MM-dd');
        const weekEndStr = format(endDate, 'yyyy-MM-dd');
        params = `week_start=${weekStartStr}&week_end=${weekEndStr}`;
        filename = `planning_${weekStartStr}_to_${weekEndStr}.pdf`;
      } else {
        params = `month=${format(currentMonth, 'yyyy-MM')}`;
        filename = `planning_${format(currentMonth, 'yyyy-MM')}.pdf`;
      }
      
      const token = localStorage.getItem('gym_token');
      
      const response = await fetch(`${API}/schedule/export/pdf?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Export failed');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success(t('exportSuccess'));
    } catch (err) {
      toast.error(t('exportError'));
    }
  };

  const handleExportExcel = async () => {
    try {
      let params;
      let filename;
      
      if (viewMode === 'week') {
        // Calculate start and end dates based on period selector
        const startDate = startOfWeek(periodStartDate, { weekStartsOn: 1 });
        let endDate;
        if (periodUnit === 'week') {
          endDate = addDays(startDate, periodLength * 7);
        } else {
          endDate = addMonths(startDate, periodLength);
        }
        const weekStartStr = format(startDate, 'yyyy-MM-dd');
        const weekEndStr = format(endDate, 'yyyy-MM-dd');
        params = `week_start=${weekStartStr}&week_end=${weekEndStr}`;
        filename = `planning_${weekStartStr}_to_${weekEndStr}.xlsx`;
      } else {
        params = `month=${format(currentMonth, 'yyyy-MM')}`;
        filename = `planning_${format(currentMonth, 'yyyy-MM')}.xlsx`;
      }
      
      const token = localStorage.getItem('gym_token');
      
      const response = await fetch(`${API}/schedule/export/excel?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Export failed');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success(t('exportSuccess'));
    } catch (err) {
      toast.error(t('exportError'));
    }
  };

  // Get absences for a day
  const getAbsencesForDay = (dayStr) => {
    return filteredAbsences.filter(a => {
      const start = a.start_date;
      const end = a.end_date;
      return dayStr >= start && dayStr <= end;
    });
  };

  // Calculate slot style with proper positioning for overlapping
  const getSlotStyle = (slot, index, total) => {
    const startMinutes = parseInt(slot.start_time.split(':')[0]) * 60 + parseInt(slot.start_time.split(':')[1]);
    const endMinutes = parseInt(slot.end_time.split(':')[0]) * 60 + parseInt(slot.end_time.split(':')[1]);
    const dayStart = workHours.start * 60;
    
    const top = ((startMinutes - dayStart) / 60) * 48;
    const height = ((endMinutes - startMinutes) / 60) * 48;
    
    const width = 100 / total;
    const left = index * width;
    
    return {
      top: `${top}px`,
      height: `${Math.max(height, 24)}px`,
      left: `${left}%`,
      width: `${width}%`,
      backgroundColor: slot.user_color || '#3B82F6'
    };
  };

  // Get slots for a day grouped by overlap
  const getSlotsByDay = (dayStr) => {
    const daySlots = filteredSchedule.filter(s => s.date === dayStr);
    
    // Sort by start time
    daySlots.sort((a, b) => a.start_time.localeCompare(b.start_time));
    
    // Find overlapping groups
    const groups = [];
    const used = new Set();
    
    daySlots.forEach((slot, i) => {
      if (used.has(i)) return;
      
      const slotStart = parseInt(slot.start_time.split(':')[0]) * 60 + parseInt(slot.start_time.split(':')[1]);
      const slotEnd = parseInt(slot.end_time.split(':')[0]) * 60 + parseInt(slot.end_time.split(':')[1]);
      
      const group = [slot];
      used.add(i);
      
      // Find all overlapping slots
      daySlots.forEach((other, j) => {
        if (used.has(j)) return;
        
        const otherStart = parseInt(other.start_time.split(':')[0]) * 60 + parseInt(other.start_time.split(':')[1]);
        const otherEnd = parseInt(other.end_time.split(':')[0]) * 60 + parseInt(other.end_time.split(':')[1]);
        
        // Check if any slot in group overlaps with other
        const overlaps = group.some(g => {
          const gStart = parseInt(g.start_time.split(':')[0]) * 60 + parseInt(g.start_time.split(':')[1]);
          const gEnd = parseInt(g.end_time.split(':')[0]) * 60 + parseInt(g.end_time.split(':')[1]);
          return otherStart < gEnd && otherEnd > gStart;
        });
        
        if (overlaps) {
          group.push(other);
          used.add(j);
        }
      });
      
      groups.push(group);
    });
    
    return groups;
  };

  const getEventsByDay = (dayStr) => events.filter(e => e.date === dayStr);
  const getHoliday = (dayStr) => holidays[dayStr];

  // Calculate net hours (minus break)
  const calculateNetHours = (slot) => {
    const startMinutes = parseInt(slot.start_time.split(':')[0]) * 60 + parseInt(slot.start_time.split(':')[1]);
    const endMinutes = parseInt(slot.end_time.split(':')[0]) * 60 + parseInt(slot.end_time.split(':')[1]);
    const grossMinutes = endMinutes - startMinutes;
    const netMinutes = grossMinutes - (slot.break_minutes || 0);
    return (netMinutes / 60).toFixed(2);
  };

  // Month view helpers
  const monthDays = getDaysInMonth(currentMonth);
  const firstDayOfMonth = getDay(currentMonth);
  const adjustedFirstDay = firstDayOfMonth === 0 ? 6 : firstDayOfMonth - 1;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <TooltipProvider>
      <div className="space-y-4" data-testid="schedule-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl">{t('schedule').toUpperCase()}</h1>
            <p className="text-muted-foreground text-sm">
              {viewMode === 'week' 
                ? `${t('weekOf')} ${format(currentWeek, 'd MMMM yyyy', { locale })}`
                : format(currentMonth, 'MMMM yyyy', { locale })}
            </p>
          </div>
          
          <div className="flex items-center gap-2 flex-wrap">
            <Tabs value={viewMode} onValueChange={setViewMode}>
              <TabsList>
                <TabsTrigger value="week" data-testid="week-view-btn">{t('weekView')}</TabsTrigger>
                <TabsTrigger value="month" data-testid="month-view-btn">{t('monthView')}</TabsTrigger>
              </TabsList>
            </Tabs>
            
            {/* Layout mode selector (vertical/horizontal) */}
            {viewMode === 'week' && (
              <div className="flex items-center border rounded-md">
                <Button 
                  variant={layoutMode === 'vertical' ? 'default' : 'ghost'} 
                  size="sm"
                  onClick={() => setLayoutMode('vertical')}
                  className="rounded-r-none"
                  title={t('verticalLayout')}
                  data-testid="vertical-layout-btn"
                >
                  <Columns3 className="h-4 w-4" />
                </Button>
                <Button 
                  variant={layoutMode === 'horizontal' ? 'default' : 'ghost'} 
                  size="sm"
                  onClick={() => setLayoutMode('horizontal')}
                  className="rounded-l-none"
                  title={t('horizontalLayout')}
                  data-testid="horizontal-layout-btn"
                >
                  <Rows3 className="h-4 w-4" />
                </Button>
              </div>
            )}
            
            {/* Filter */}
            <Popover open={filterPopoverOpen} onOpenChange={setFilterPopoverOpen}>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" data-testid="filter-btn">
                  <Filter className="h-4 w-4 mr-2" />
                  {t('filter')} ({selectedUsers.length})
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-64" align="end">
                <div className="space-y-3">
                  <span className="text-sm font-medium">{t('selectCollaborators')}</span>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={selectAllUsers}>{t('selectAll')}</Button>
                    <Button variant="outline" size="sm" onClick={deselectAllUsers}>{t('deselectAll')}</Button>
                  </div>
                  <div className="max-h-48 overflow-y-auto space-y-2">
                    {team.map(member => (
                      <div key={member.id} className="flex items-center space-x-2">
                        <Checkbox
                          id={`filter-${member.id}`}
                          checked={selectedUsers.includes(member.id)}
                          onCheckedChange={(checked) => handleUserFilterChange(member.id, checked)}
                        />
                        <label htmlFor={`filter-${member.id}`} className="flex items-center gap-2 text-sm cursor-pointer">
                          <span className="w-3 h-3 rounded-full" style={{ backgroundColor: member.color || '#3B82F6' }} />
                          {member.first_name} {member.last_name}
                          {member.id === user?.id && <span className="text-xs text-muted-foreground">({t('you')})</span>}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              </PopoverContent>
            </Popover>
            
            {isManager && (
              <>
                <Button variant="outline" size="sm" onClick={() => handleOpenEventDialog()} data-testid="new-event-btn">
                  <CalendarPlus className="h-4 w-4 mr-2" />
                  {t('newEvent')}
                </Button>
                {viewMode === 'week' && (
                  <Button variant="outline" size="sm" onClick={openCopyDialog} data-testid="copy-week-btn">
                    <Copy className="h-4 w-4 mr-2" />
                    {t('copyWeek')}
                  </Button>
                )}
                <Button variant="outline" size="sm" onClick={handleExportPDF} data-testid="export-pdf-btn">
                  <FileDown className="h-4 w-4 mr-2" />
                  PDF
                </Button>
                <Button variant="outline" size="sm" onClick={handleExportExcel} data-testid="export-excel-btn">
                  <FileSpreadsheet className="h-4 w-4 mr-2" />
                  Excel
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Totals */}
        <div className="grid grid-cols-2 gap-4">
          <div className="stat-card">
            <p className="stat-label">{t('weeklyTotal')} ({t('mondayToSunday')})</p>
            <p className="stat-value">{totals.week_hours}{t('h')}</p>
          </div>
          <div className="stat-card">
            <p className="stat-label">{t('monthlyTotal')} ({t('calendarMonth')})</p>
            <p className="stat-value">{totals.month_hours}{t('h')}</p>
          </div>
        </div>

        {/* Navigation with Calendar */}
        {viewMode === 'week' ? (
          <div className="flex flex-wrap items-center justify-between gap-4 border border-border rounded-lg p-2 bg-card">
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" onClick={handlePrevWeek}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                <PopoverTrigger asChild>
                  <Button variant="ghost" className="font-medium" data-testid="calendar-picker-btn">
                    <CalendarIcon className="h-4 w-4 mr-2" />
                    {format(currentWeek, 'd MMM', { locale })} - {format(addDays(currentWeek, 6), 'd MMM yyyy', { locale })}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="center">
                  <Calendar mode="single" selected={currentWeek} onSelect={handleDateSelect} locale={locale} weekStartsOn={1} />
                </PopoverContent>
              </Popover>
              <Button variant="ghost" size="sm" onClick={handleNextWeek}>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
            
            {/* Period Selector */}
            <div className="flex items-center gap-2 text-sm">
              <span className="text-muted-foreground">{t('displayPeriod')}</span>
              <select 
                value={periodLength} 
                onChange={(e) => setPeriodLength(parseInt(e.target.value))}
                className="border border-border rounded px-2 py-1 bg-background text-sm"
              >
                <option value="1">1</option>
                <option value="2">2</option>
                <option value="3">3</option>
                <option value="4">4</option>
              </select>
              <select 
                value={periodUnit} 
                onChange={(e) => setPeriodUnit(e.target.value)}
                className="border border-border rounded px-2 py-1 bg-background text-sm"
              >
                <option value="week">{t('weeksFrom')}</option>
                <option value="month">{t('monthsFrom')}</option>
              </select>
              <Popover open={periodCalendarOpen} onOpenChange={setPeriodCalendarOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm" className="h-8" data-testid="period-calendar-btn">
                    <CalendarIcon className="h-3 w-3 mr-1" />
                    {format(periodStartDate, 'dd/MM/yyyy', { locale })}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="end">
                  <Calendar 
                    mode="single" 
                    selected={periodStartDate} 
                    onSelect={(date) => { setPeriodStartDate(date || new Date()); setPeriodCalendarOpen(false); setCurrentWeek(startOfWeek(date || new Date(), { weekStartsOn: 1 })); }}
                    locale={locale} 
                    weekStartsOn={1} 
                  />
                </PopoverContent>
              </Popover>
              <Button 
                variant="outline" 
                size="sm" 
                className="h-8 text-xs"
                onClick={() => { setPeriodStartDate(new Date()); setCurrentWeek(startOfWeek(new Date(), { weekStartsOn: 1 })); }}
              >
                {t('today')}
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-between border border-border rounded-lg p-2 bg-card">
            <Button variant="ghost" size="sm" onClick={handlePrevMonth}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
              <PopoverTrigger asChild>
                <Button variant="ghost" className="font-medium">
                  <CalendarIcon className="h-4 w-4 mr-2" />
                  {format(currentMonth, 'MMMM yyyy', { locale })}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="center">
                <Calendar mode="single" selected={currentMonth} onSelect={handleDateSelect} locale={locale} weekStartsOn={1} />
              </PopoverContent>
            </Popover>
            <Button variant="ghost" size="sm" onClick={handleNextMonth}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        )}

        {/* Week Grid - Vertical Layout */}
        {viewMode === 'week' && layoutMode === 'vertical' && (
          <div className="border border-border rounded-lg overflow-x-auto">
            <div className="min-w-[900px]">
              {/* Header */}
              <div className="grid grid-cols-8 border-b border-border bg-muted/50">
                <div className="p-3 text-xs font-semibold uppercase tracking-wider text-center border-r border-border">
                  {t('hour')}
                </div>
                {days.map((day, i) => {
                  const dayStr = format(day, 'yyyy-MM-dd');
                  const holiday = getHoliday(dayStr);
                  const dayAbsences = getAbsencesForDay(dayStr);
                  
                  return (
                    <div 
                      key={i} 
                      className={`p-2 text-xs font-semibold uppercase tracking-wider text-center border-r border-border last:border-r-0 ${holiday ? 'bg-amber-50 dark:bg-amber-950' : ''}`}
                    >
                      <div>{format(day, 'EEE', { locale })}</div>
                      <div className="text-lg">{format(day, 'd')}</div>
                      {holiday && (
                        <div className="text-[10px] font-normal text-amber-600 dark:text-amber-400 flex items-center justify-center gap-1">
                          <Star className="h-2.5 w-2.5" />
                          {holiday[language] || holiday.fr}
                        </div>
                      )}
                      {/* Absences indicator */}
                      {dayAbsences.length > 0 && (
                        <div className="mt-1 space-y-0.5">
                          {dayAbsences.slice(0, 2).map(absence => (
                            <Tooltip key={absence.id}>
                              <TooltipTrigger asChild>
                                <div 
                                  className={`text-[9px] px-1 rounded truncate ${absence.status === 'approved' ? 'text-white' : 'text-foreground border'}`}
                                  style={{ 
                                    backgroundColor: absence.status === 'approved' ? (absence.user_color || '#3B82F6') : 'transparent',
                                    borderColor: absence.user_color || '#3B82F6',
                                    opacity: absence.status === 'approved' ? 1 : 0.6
                                  }}
                                >
                                  {absence.user_name?.split(' ')[0]}
                                </div>
                              </TooltipTrigger>
                              <TooltipContent>
                                <div className="text-xs">
                                  <p className="font-medium">{absence.user_name}</p>
                                  <p>{ABSENCE_TYPES[absence.absence_type]?.[language] || absence.absence_type}</p>
                                  <p className="text-muted-foreground">{absence.status === 'approved' ? t('approved') : t('pending')}</p>
                                </div>
                              </TooltipContent>
                            </Tooltip>
                          ))}
                          {dayAbsences.length > 2 && (
                            <div className="text-[9px] text-muted-foreground">+{dayAbsences.length - 2}</div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Time Grid with overlaid slots */}
              <div className="grid grid-cols-8 relative">
                {/* Hour labels column */}
                <div className="border-r border-border bg-muted/30">
                  {HOURS.map((hour) => (
                    <div 
                      key={hour} 
                      className="h-12 p-2 text-xs mono text-muted-foreground border-b border-border/50 last:border-b-0 font-medium flex items-center"
                    >
                      {hour.toString().padStart(2, '0')}:00
                    </div>
                  ))}
                </div>
                
                {/* Day columns with slots */}
                {days.slice(0, 7).map((day, dayIndex) => {
                  const dayStr = format(day, 'yyyy-MM-dd');
                  const daySlots = schedule.filter(s => s.date === dayStr);
                  const dayEvents = getEventsByDay(dayStr);
                  const holiday = getHoliday(dayStr);
                  
                  // Group slots by user for continuous display
                  const slotsByUser = {};
                  daySlots.forEach(slot => {
                    if (!slotsByUser[slot.user_id]) {
                      slotsByUser[slot.user_id] = [];
                    }
                    slotsByUser[slot.user_id].push(slot);
                  });
                  
                  // Sort users' slots by start time
                  Object.keys(slotsByUser).forEach(userId => {
                    slotsByUser[userId].sort((a, b) => a.start_time.localeCompare(b.start_time));
                  });
                  
                  // Calculate column positions for each user
                  const userColumns = {};
                  const userIds = Object.keys(slotsByUser);
                  userIds.forEach((userId, idx) => {
                    userColumns[userId] = idx;
                  });
                  const totalColumns = userIds.length || 1;
                  
                  // Fixed height: 48px per hour
                  const totalHeight = HOURS.length * 48;
                  
                  return (
                    <div 
                      key={dayIndex}
                      className={`relative border-r border-border last:border-r-0 ${holiday ? 'bg-amber-50/30 dark:bg-amber-950/20' : ''}`}
                      style={{ height: `${totalHeight}px` }}
                    >
                      {/* Hour grid lines */}
                      {HOURS.map((hour, hourIndex) => (
                        <div
                          key={hour}
                          className={`absolute left-0 right-0 h-12 border-b border-border/50 ${isManager ? 'cursor-pointer hover:bg-muted/30' : ''}`}
                          style={{ top: `${hourIndex * 48}px` }}
                          onClick={() => handleCellClick(day, hour)}
                          onDragOver={handleDragOver}
                          onDrop={(e) => handleDrop(e, day, hour)}
                        />
                      ))}
                      
                      {/* Events */}
                      {dayEvents.map(event => {
                        const eventHour = event.all_day ? workHours.start : parseInt(event.start_time?.split(':')[0] || '9');
                        const topPos = (eventHour - workHours.start) * 48;
                        return (
                          <div
                            key={event.id}
                            className="absolute left-0 right-0 mx-1 px-1 py-0.5 text-[10px] text-white rounded-sm cursor-pointer z-20 flex items-center gap-1"
                            style={{ backgroundColor: event.color, top: `${Math.max(topPos, 0) + 2}px` }}
                            onClick={(e) => handleEventClick(e, event)}
                          >
                            <CalendarPlus className="h-2.5 w-2.5" />
                            {event.title}
                          </div>
                        );
                      })}
                      
                      {/* Schedule slots - grouped by user */}
                      {Object.entries(slotsByUser).map(([userId, userSlots]) => {
                        const colIndex = userColumns[userId];
                        const colWidth = 100 / totalColumns;
                        const member = team.find(m => m.id === userId);
                        
                        return userSlots.map((slot, slotIndex) => (
                          <ScheduleSlot
                            key={slot.id}
                            slot={slot}
                            colIndex={colIndex}
                            colWidth={colWidth}
                            member={member}
                            slotIndex={slotIndex}
                            workHours={workHours}
                            isManager={isManager}
                            resizingSlot={resizingSlot}
                            handleSlotClick={handleSlotClick}
                            handleDragStart={handleDragStart}
                            handleResizeStart={handleResizeStart}
                            calculateNetHours={calculateNetHours}
                            t={t}
                          />
                        ));
                      })}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Week Grid - Horizontal Layout (rows = days, columns = hours, slots stacked) */}
        {viewMode === 'week' && layoutMode === 'horizontal' && (
          <div className="border border-border rounded-lg overflow-x-auto">
            <div style={{ minWidth: `${140 + (HOURS.length * 80)}px` }}>
              {/* Header with hours */}
              <div className="flex border-b border-border bg-muted/50 sticky top-0 z-10">
                <div className="w-36 flex-shrink-0 p-2 border-r border-border font-semibold text-sm">
                  {t('day')}
                </div>
                {HOURS.map((hour) => (
                  <div 
                    key={hour} 
                    className="w-20 flex-shrink-0 p-2 text-xs font-semibold text-center border-r border-border"
                  >
                    {hour.toString().padStart(2, '0')}:00
                  </div>
                ))}
              </div>
              
              {/* Rows for each day */}
              {days.map((day, dayIndex) => {
                const dayStr = format(day, 'yyyy-MM-dd');
                const holiday = getHoliday(dayStr);
                const dayAbsences = getAbsencesForDay(dayStr);
                const daySlots = schedule.filter(s => s.date === dayStr);
                
                // Group slots by user for continuous display
                const slotsByUser = {};
                daySlots.forEach(slot => {
                  if (!slotsByUser[slot.user_id]) {
                    slotsByUser[slot.user_id] = [];
                  }
                  slotsByUser[slot.user_id].push(slot);
                });
                
                // Sort slots by start time within each user
                Object.keys(slotsByUser).forEach(userId => {
                  slotsByUser[userId].sort((a, b) => a.start_time.localeCompare(b.start_time));
                });
                
                // Group absences by user
                const absencesByUser = {};
                dayAbsences.forEach(abs => {
                  if (!absencesByUser[abs.user_id]) {
                    absencesByUser[abs.user_id] = [];
                  }
                  absencesByUser[abs.user_id].push(abs);
                });
                
                return (
                  <div 
                    key={dayIndex} 
                    className={`flex border-b border-border last:border-b-0 ${holiday ? 'bg-amber-50/50 dark:bg-amber-950/30' : ''}`}
                  >
                    {/* Day label */}
                    <div className="w-36 flex-shrink-0 p-2 border-r border-border bg-card">
                      <div className="font-semibold text-sm">{format(day, 'EEE', { locale })}</div>
                      <div className="text-lg font-bold">{format(day, 'd MMM', { locale })}</div>
                      {holiday && (
                        <div className="text-[10px] text-amber-600 dark:text-amber-400 flex items-center gap-1 mt-1">
                          <Star className="h-2.5 w-2.5" />
                          {holiday[language] || holiday.fr}
                        </div>
                      )}
                    </div>
                    
                    {/* Timeline grid with absolute positioned slots */}
                    <div className="relative flex-1" style={{ minHeight: '80px' }}>
                      {/* Hour grid lines */}
                      {HOURS.map((hour) => (
                        <div 
                          key={hour}
                          className={`absolute top-0 bottom-0 w-20 border-r border-border ${isManager ? 'hover:bg-muted/20' : ''}`}
                          style={{ left: `${(hour - workHours.start) * 80}px` }}
                          onClick={() => isManager && handleCellClick(day, hour)}
                          onDragOver={(e) => {
                            if (!isManager || !draggedSlot) return;
                            e.preventDefault();
                            e.currentTarget.classList.add('bg-primary/20');
                          }}
                          onDragLeave={(e) => {
                            e.currentTarget.classList.remove('bg-primary/20');
                          }}
                          onDrop={(e) => {
                            e.preventDefault();
                            e.currentTarget.classList.remove('bg-primary/20');
                            handleDrop(e, day, hour);
                          }}
                        />
                      ))}
                      
                      {/* Absences as continuous blocks */}
                      {Object.entries(absencesByUser).map(([userId, userAbsences]) => {
                        const member = team.find(m => m.id === userId);
                        return userAbsences.map((absence, idx) => {
                          const absStart = absence.day_part === 'afternoon' ? 12 : workHours.start;
                          const absEnd = absence.day_part === 'morning' ? 12 : workHours.end;
                          const left = (absStart - workHours.start) * 80;
                          const width = (absEnd - absStart) * 80;
                          
                          return (
                            <div
                              key={`abs-${absence.id}`}
                              className={`absolute h-6 rounded px-1 flex items-center text-[10px] truncate ${absence.status === 'approved' ? 'bg-red-500 text-white' : 'bg-red-100 text-red-700 border border-red-300'}`}
                              style={{ 
                                left: `${left}px`, 
                                width: `${width - 4}px`,
                                top: `${4 + idx * 28}px`
                              }}
                              title={`${absence.user_name} - ${t(absence.type)}`}
                            >
                              {member?.first_name} - {t(absence.type)}
                            </div>
                          );
                        });
                      })}
                      
                      {/* Slots as continuous blocks per user */}
                      {Object.entries(slotsByUser).map(([userId, userSlots], userIdx) => {
                        const member = team.find(m => m.id === userId);
                        const absCount = absencesByUser[userId]?.length || 0;
                        const baseTop = 4 + absCount * 28;
                        
                        return userSlots.map((slot, slotIdx) => {
                          const [startH, startM] = slot.start_time.split(':').map(Number);
                          const [endH, endM] = slot.end_time.split(':').map(Number);
                          const startPos = (startH - workHours.start) * 80 + (startM / 60) * 80;
                          const endPos = (endH - workHours.start) * 80 + (endM / 60) * 80;
                          const width = endPos - startPos;
                          
                          return (
                            <Tooltip key={slot.id}>
                              <TooltipTrigger asChild>
                                <div
                                  className={`absolute h-6 rounded px-2 flex items-center justify-between text-[11px] text-white ${slot.has_pending_correction ? 'ring-2 ring-amber-500' : ''} ${isManager ? 'cursor-move' : 'cursor-pointer'}`}
                                  style={{ 
                                    left: `${startPos}px`, 
                                    width: `${Math.max(width - 4, 60)}px`,
                                    top: `${baseTop + (userIdx * 28) + (slotIdx * 28)}px`,
                                    backgroundColor: member?.color || '#3B82F6'
                                  }}
                                  draggable={isManager}
                                  onDragStart={(e) => handleDragStart(e, slot)}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleSlotClick(e, slot);
                                  }}
                                >
                                  <span className="truncate font-medium">
                                    {slot.user_name?.split(' ')[0]} {slot.start_time}-{slot.end_time}
                                  </span>
                                  <div className="flex items-center gap-0.5 ml-1">
                                    {slot.comment && <Star className="h-3 w-3 comment-star-yellow" />}
                                    {slot.break_minutes > 0 && <Coffee className="h-3 w-3" />}
                                    {slot.has_pending_correction && <AlertCircle className="h-3 w-3" />}
                                  </div>
                                </div>
                              </TooltipTrigger>
                              <TooltipContent side="top">
                                <div className="text-xs space-y-1">
                                  <p className="font-medium">{slot.user_name}</p>
                                  <p><Clock className="h-3 w-3 inline mr-1" />{slot.start_time} - {slot.end_time}</p>
                                  {slot.break_minutes > 0 && (
                                    <p><Coffee className="h-3 w-3 inline mr-1" />{t('break')}: {slot.break_minutes} min</p>
                                  )}
                                  <p className="text-muted-foreground">{t('netHours')}: {calculateNetHours(slot)}h</p>
                                  {slot.comment && (
                                    <p className="border-t pt-1 mt-1"><Star className="h-3 w-3 inline mr-1 text-yellow-400" />{slot.comment}</p>
                                  )}
                                </div>
                              </TooltipContent>
                            </Tooltip>
                          );
                        });
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Month Grid */}
        {viewMode === 'month' && (
          <div className="border border-border rounded-lg overflow-hidden">
            <div className="grid grid-cols-7 bg-muted/50">
              {[t('monday'), t('tuesday'), t('wednesday'), t('thursday'), t('friday'), t('saturday'), t('sunday')].map((day) => (
                <div key={day} className="p-2 text-xs font-semibold uppercase tracking-wider text-center border-b border-border">
                  {day}
                </div>
              ))}
            </div>
            <div className="grid grid-cols-7">
              {Array.from({ length: adjustedFirstDay }).map((_, i) => (
                <div key={`empty-${i}`} className="min-h-[100px] border-b border-r border-border bg-muted/20" />
              ))}
              {Array.from({ length: monthDays }).map((_, i) => {
                const dayDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), i + 1);
                const dayStr = format(dayDate, 'yyyy-MM-dd');
                const daySlots = filteredSchedule.filter(s => s.date === dayStr);
                const dayEvents = getEventsByDay(dayStr);
                const dayAbsences = getAbsencesForDay(dayStr);
                const holiday = getHoliday(dayStr);
                
                return (
                  <div 
                    key={i}
                    className={`min-h-[100px] border-b border-r border-border p-1 hover:bg-muted/30 transition-colors ${holiday ? 'bg-amber-50 dark:bg-amber-950/30' : ''} ${isManager ? 'cursor-pointer' : ''}`}
                    onClick={() => isManager && handleOpenEventDialog(dayDate)}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium">{i + 1}</span>
                      {holiday && (
                        <span className="text-[9px] text-amber-600 dark:text-amber-400">{holiday[language] || holiday.fr}</span>
                      )}
                    </div>
                    
                    {/* Events */}
                    {dayEvents.slice(0, 2).map(event => (
                      <div 
                        key={event.id}
                        className="text-[10px] text-white px-1 py-0.5 rounded-sm mb-0.5 truncate"
                        style={{ backgroundColor: event.color }}
                        onClick={(e) => handleEventClick(e, event)}
                      >
                        {event.title}
                      </div>
                    ))}
                    
                    {/* Absences */}
                    {dayAbsences.slice(0, 1).map(absence => (
                      <Tooltip key={absence.id}>
                        <TooltipTrigger asChild>
                          <div 
                            className={`text-[10px] px-1 py-0.5 rounded-sm mb-0.5 truncate ${absence.status === 'approved' ? 'text-white' : 'border'}`}
                            style={{ 
                              backgroundColor: absence.status === 'approved' ? (absence.user_color || '#EF4444') : 'transparent',
                              borderColor: absence.user_color || '#EF4444',
                              opacity: absence.status === 'approved' ? 0.8 : 0.5
                            }}
                          >
                            {absence.user_name?.split(' ')[0]} - {ABSENCE_TYPES[absence.absence_type]?.[language]?.substring(0, 3) || '...'}
                          </div>
                        </TooltipTrigger>
                        <TooltipContent>
                          <div className="text-xs">
                            <p className="font-medium">{absence.user_name}</p>
                            <p>{ABSENCE_TYPES[absence.absence_type]?.[language] || absence.absence_type}</p>
                            <p>{absence.status === 'approved' ? t('approved') : t('pending')}</p>
                          </div>
                        </TooltipContent>
                      </Tooltip>
                    ))}
                    
                    {/* Slots */}
                    <div className="space-y-0.5">
                      {daySlots.slice(0, 3).map(slot => (
                        <Tooltip key={slot.id}>
                          <TooltipTrigger asChild>
                            <div 
                              className="text-[10px] px-1 py-0.5 rounded-sm truncate text-white flex items-center gap-1"
                              style={{ backgroundColor: slot.user_color || '#3B82F6' }}
                              onClick={(e) => handleSlotClick(e, slot)}
                            >
                              {slot.comment && <MessageSquare className="h-2 w-2" />}
                              {slot.user_name?.split(' ')[0]} {slot.start_time}
                            </div>
                          </TooltipTrigger>
                          <TooltipContent>
                            <div className="text-xs">
                              <p className="font-medium">{slot.user_name}</p>
                              <p>{slot.start_time} - {slot.end_time}</p>
                              {slot.break_minutes > 0 && <p>{t('break')}: {slot.break_minutes} min</p>}
                              {slot.comment && <p className="italic">{slot.comment}</p>}
                            </div>
                          </TooltipContent>
                        </Tooltip>
                      ))}
                      {(daySlots.length + dayEvents.length + dayAbsences.length) > 4 && (
                        <div className="text-[9px] text-muted-foreground">+{daySlots.length + dayEvents.length + dayAbsences.length - 4} {t('more')}</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Add/Edit Slot Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{editingSlot ? t('editSlot').toUpperCase() : t('addSlot').toUpperCase()}</DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 pt-4">
              <div className="space-y-2">
                <Label>{t('collaborator')}</Label>
                <Select value={newSlot.user_id} onValueChange={(v) => setNewSlot({ ...newSlot, user_id: v })}>
                  <SelectTrigger><SelectValue placeholder={t('collaborator')} /></SelectTrigger>
                  <SelectContent>
                    {team.map(member => (
                      <SelectItem key={member.id} value={member.id}>
                        <span className="flex items-center gap-2">
                          <span className="w-3 h-3 rounded-full" style={{ backgroundColor: member.color || '#3B82F6' }} />
                          {member.first_name} {member.last_name}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>{t('date')}</Label>
                <Input type="date" value={newSlot.date} onChange={(e) => setNewSlot({ ...newSlot, date: e.target.value })} />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t('startTime')}</Label>
                  <Select value={newSlot.start_time} onValueChange={(v) => setNewSlot({ ...newSlot, start_time: v })}>
                    <SelectTrigger className="mono"><SelectValue /></SelectTrigger>
                    <SelectContent className="max-h-[200px]">
                      {TIME_SLOTS.map(time => (<SelectItem key={time} value={time} className="mono">{time}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>{t('endTime')}</Label>
                  <Select value={newSlot.end_time} onValueChange={(v) => setNewSlot({ ...newSlot, end_time: v })}>
                    <SelectTrigger className="mono"><SelectValue /></SelectTrigger>
                    <SelectContent className="max-h-[200px]">
                      {TIME_SLOTS.map(time => (<SelectItem key={time} value={time} className="mono">{time}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Coffee className="h-4 w-4" />
                  {t('breakMinutes')}
                </Label>
                <Input 
                  type="number" 
                  min="0" 
                  max="120"
                  value={newSlot.break_minutes} 
                  onChange={(e) => setNewSlot({ ...newSlot, break_minutes: parseInt(e.target.value) || 0 })}
                  placeholder="0"
                />
              </div>

              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <MessageSquare className="h-4 w-4" />
                  {t('comment')}
                </Label>
                <Textarea 
                  value={newSlot.comment} 
                  onChange={(e) => setNewSlot({ ...newSlot, comment: e.target.value })}
                  placeholder={t('commentPlaceholder')}
                  rows={2}
                />
              </div>

              <div className="flex gap-2 pt-4">
                <Button onClick={handleSaveSlot} className="flex-1">
                  {editingSlot ? t('update') : t('add')}
                </Button>
                {editingSlot && (
                  <Button variant="destructive" onClick={handleDeleteSlot}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Copy Dialog */}
        <Dialog open={copyDialogOpen} onOpenChange={setCopyDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{t('copySchedule').toUpperCase()}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-4">
              {/* Source week */}
              <div className="space-y-2">
                <Label>{t('sourceWeek')}</Label>
                <Popover open={copySourceCalendarOpen} onOpenChange={setCopySourceCalendarOpen}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start">
                      <CalendarIcon className="h-4 w-4 mr-2" />
                      {copySourceWeek ? `${format(copySourceWeek, 'd MMM', { locale })} - ${format(addDays(copySourceWeek, 6), 'd MMM yyyy', { locale })}` : t('selectWeek')}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar 
                      mode="single" 
                      selected={copySourceWeek} 
                      onSelect={(date) => { setCopySourceWeek(startOfWeek(date, { weekStartsOn: 1 })); setCopySourceCalendarOpen(false); }}
                      locale={locale}
                      weekStartsOn={1}
                    />
                  </PopoverContent>
                </Popover>
              </div>

              {/* Target weeks */}
              <div className="space-y-2">
                <Label>{t('targetWeeks')} ({copyTargetWeeks.length} {t('selected')})</Label>
                <div className="border border-border rounded-lg max-h-[250px] overflow-y-auto">
                  {generateTargetWeeks().map(week => (
                    <div 
                      key={week.date}
                      className={`flex items-center gap-2 p-2 hover:bg-muted/50 cursor-pointer border-b border-border last:border-b-0 ${copyTargetWeeks.includes(week.date) ? 'bg-primary/10' : ''}`}
                      onClick={() => toggleCopyTargetWeek(week.date)}
                    >
                      <Checkbox checked={copyTargetWeeks.includes(week.date)} />
                      <span className="text-sm">{week.label}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex gap-2 pt-4">
                <Button variant="outline" onClick={() => setCopyDialogOpen(false)} className="flex-1">{t('cancel')}</Button>
                <Button onClick={handleCopyWeek} className="flex-1" disabled={copyTargetWeeks.length === 0}>
                  {t('confirm')} ({copyTargetWeeks.length})
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Event Dialog */}
        <Dialog open={eventDialogOpen} onOpenChange={setEventDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingEvent ? t('editEvent') : t('newEvent')}</DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 pt-4">
              <div className="space-y-2">
                <Label>{t('eventTitle')}</Label>
                <Input value={newEvent.title} onChange={(e) => setNewEvent({ ...newEvent, title: e.target.value })} />
              </div>

              <div className="space-y-2">
                <Label>{t('date')}</Label>
                <Input type="date" value={newEvent.date} onChange={(e) => setNewEvent({ ...newEvent, date: e.target.value })} />
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox id="all-day" checked={newEvent.all_day} onCheckedChange={(checked) => setNewEvent({ ...newEvent, all_day: checked })} />
                <Label htmlFor="all-day">{t('allDay')}</Label>
              </div>

              {!newEvent.all_day && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{t('startTime')}</Label>
                    <Select value={newEvent.start_time} onValueChange={(v) => setNewEvent({ ...newEvent, start_time: v })}>
                      <SelectTrigger className="mono"><SelectValue /></SelectTrigger>
                      <SelectContent className="max-h-[200px]">
                        {TIME_SLOTS.map(time => (<SelectItem key={time} value={time}>{time}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>{t('endTime')}</Label>
                    <Select value={newEvent.end_time} onValueChange={(v) => setNewEvent({ ...newEvent, end_time: v })}>
                      <SelectTrigger className="mono"><SelectValue /></SelectTrigger>
                      <SelectContent className="max-h-[200px]">
                        {TIME_SLOTS.map(time => (<SelectItem key={time} value={time}>{time}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <Label>{t('eventColor')}</Label>
                <div className="flex gap-2">
                  {EVENT_COLORS.map(color => (
                    <button
                      key={color}
                      className={`w-8 h-8 rounded-full transition-transform ${newEvent.color === color ? 'ring-2 ring-offset-2 ring-primary scale-110' : ''}`}
                      style={{ backgroundColor: color }}
                      onClick={() => setNewEvent({ ...newEvent, color })}
                    />
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <Label>{t('eventDescription')}</Label>
                <Textarea value={newEvent.description} onChange={(e) => setNewEvent({ ...newEvent, description: e.target.value })} />
              </div>

              <div className="flex gap-2 pt-4">
                <Button onClick={handleSaveEvent} className="flex-1">
                  {editingEvent ? t('update') : t('add')}
                </Button>
                {editingEvent && (
                  <Button variant="destructive" onClick={handleDeleteEvent}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Correction Dialog */}
        <Dialog open={correctionDialogOpen} onOpenChange={setCorrectionDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t('correctTime').toUpperCase()}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-4">
              {editingSlot && (
                <div className="p-3 bg-muted rounded-lg text-sm">
                  <p><strong>{t('plannedHours')}:</strong> {editingSlot.start_time} - {editingSlot.end_time}</p>
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t('actualStartTime')}</Label>
                  <Select value={correction.actual_start_time} onValueChange={(v) => setCorrection({ ...correction, actual_start_time: v })}>
                    <SelectTrigger className="mono"><SelectValue /></SelectTrigger>
                    <SelectContent className="max-h-[200px]">
                      {TIME_SLOTS.map(time => (<SelectItem key={time} value={time}>{time}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>{t('actualEndTime')}</Label>
                  <Select value={correction.actual_end_time} onValueChange={(v) => setCorrection({ ...correction, actual_end_time: v })}>
                    <SelectTrigger className="mono"><SelectValue /></SelectTrigger>
                    <SelectContent className="max-h-[200px]">
                      {TIME_SLOTS.map(time => (<SelectItem key={time} value={time}>{time}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label>{t('correctionReason')}</Label>
                <Textarea value={correction.reason} onChange={(e) => setCorrection({ ...correction, reason: e.target.value })} />
              </div>
              <Button onClick={handleSubmitCorrection} className="w-full">{t('submitCorrection')}</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </TooltipProvider>
  );
}
